import { InputHTMLAttributes, forwardRef, useEffect, useId, useRef } from 'react'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  error?: string
  indeterminate?: boolean
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, indeterminate, className = '', id, ...props }, ref) => {
    const internalRef = useRef<HTMLInputElement | null>(null)
    // Fall back to a generated id so the label can be associated via htmlFor
    const generatedId = useId()
    const checkboxId = id ?? generatedId

    // Merge forwarded ref with internal ref
    const setRefs = (el: HTMLInputElement | null) => {
      internalRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref) (ref as { current: HTMLInputElement | null }).current = el
    }

    useEffect(() => {
      if (internalRef.current) {
        internalRef.current.indeterminate = !!indeterminate
      }
    }, [indeterminate])

    return (
      <div className={label || error ? 'w-full' : 'inline-flex'}>
        <div className="flex items-center">
          <input
            ref={setRefs}
            id={checkboxId}
            type="checkbox"
            className={`
              h-4 w-4 flex-shrink-0
              text-primary
              bg-background
              border-input
              rounded
              focus:ring-2 focus:ring-ring focus:ring-offset-0
              disabled:opacity-50 disabled:cursor-not-allowed
              ${className}
            `}
            {...props}
          />
          {label && (
            <label htmlFor={checkboxId} className="ml-2 cursor-pointer text-sm text-foreground">
              {label}
            </label>
          )}
        </div>
        {error && (
          <p className="mt-1 text-xs text-destructive">{error}</p>
        )}
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'

export default Checkbox
